/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    CompanyDetailsActionType,
    CompanyDetailsActions,
    fetchCompanyCardDataErrorAction,
    fetchCompanyDetailsSuccessAction,
    fetchCompanyStoresListSuccessAction,
    fetchCompanyCashboxesStatsSuccessAction,
    fetchCompanyTurnoversByPeriodErrorAction,
    fetchCompanyTurnoversByPeriodSuccessAction,
    fetchCompanyTurnoversByObjectsSuccessAction,
    fetchCompanyTurnoversByObjectsErrorAction,
    downloadTurnoversByPeriodSuccessAction,
    downloadTurnoversByPeriodErrorAction,
    downloadTurnoversByObjectsSuccessAction,
    downloadTurnoversByObjectsErrorAction,
    fetchCompanyCashBoxesStatsErrorAction,
} from '../actions/companyDetailsActions';
import { catchError, exhaustMap, map, mapTo, switchMap, tap, takeUntil } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { companiesClient } from '../api/companies/companiesClient';
import { AxiosError } from 'axios';
import { downloadDataAsFile, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';

export const getCompanyDetailsEpic: Epic<CompanyDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(CompanyDetailsActionType.OPEN_REGISTRY_COMPANY_CARD_PAGE),
        switchMap(() => {
            const { CompanyTaxNumber: companyTaxNumber } = state$.value.routerState.route.params;

            return from(companiesClient.getCompany(companyTaxNumber)).pipe(
                map((resp) => fetchCompanyDetailsSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchCompanyCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.companyNotFound', { searchText: companyTaxNumber }),
                            ),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(CompanyDetailsActionType.EXIT_REGISTRY_COMPANY_CARD_PAGE))),
            );
        }),
    );
};

export const getCompanyCashBoxesStatsEpic: Epic<CompanyDetailsActions> = (action$) => {
    return action$.pipe(
        ofType(CompanyDetailsActionType.FETCH_COMPANY_CASHBOXES_STATS),
        switchMap((action) => {
            const companyTaxNumber = action.payload;

            return from(companiesClient.getCompanyCashBoxesStats(companyTaxNumber)).pipe(
                map((resp) => {
                    return fetchCompanyCashboxesStatsSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCompanyCashBoxesStatsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashBoxesStatsNotFound')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(CompanyDetailsActionType.EXIT_REGISTRY_COMPANY_CARD_PAGE))),
            );
        }),
    );
};

export const getCompanyStoresListEpic: Epic<CompanyDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CompanyDetailsActionType.OPEN_REGISTRY_COMPANY_CARD_PAGE,
            CompanyDetailsActionType.APPLY_STORES_FILTER,
            CompanyDetailsActionType.APPLY_STORES_PAGING,
        ),
        switchMap(() => {
            const { CompanyTaxNumber: companyTaxNumber } = state$.value.routerState.route.params;
            const queryParams = {
                ...state$.value.companyDetailsReducer.storesPageParams,
                ...state$.value.companyDetailsReducer.storesFilterParams,
            };

            return from(companiesClient.getCompanyStores({ tax_number: companyTaxNumber, params: queryParams })).pipe(
                map((resp) => fetchCompanyStoresListSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchCompanyCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.storesRequestFailed', { searchText: companyTaxNumber }),
                            ),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(CompanyDetailsActionType.EXIT_REGISTRY_COMPANY_CARD_PAGE))),
            );
        }),
    );
};

export const getCompanyTurnoversByPeriodEpic: Epic<CompanyDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_PERIOD),

        switchMap((action) => {
            const { CompanyTaxNumber } = state$.value.routerState.route.params;

            return from(companiesClient.getCompanyTurnoversByPeriod(CompanyTaxNumber, action.payload)).pipe(
                map((resp) => {
                    return fetchCompanyTurnoversByPeriodSuccessAction({ turnovers: resp, params: action.payload });
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCompanyTurnoversByPeriodErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.turnoversRequestFailed')),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const getCompanyTurnoversByObjectsEpic: Epic<CompanyDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_OBJECTS,
            CompanyDetailsActionType.APPLY_TURNOVERS_BY_OBJECTS_PAGING,
            CompanyDetailsActionType.APPLY_TURNOVERS_BY_OBJECTS_FILTER,
        ),

        switchMap(() => {
            const { CompanyTaxNumber } = state$.value.routerState.route.params;
            const queryParams = {
                ...state$.value.companyDetailsReducer.turnoversByObjectsPageParams,
                ...state$.value.companyDetailsReducer.turnoversByObjectsFilterParams,
            };

            return from(companiesClient.getCompanyTurnoversByObjects(CompanyTaxNumber, queryParams)).pipe(
                map((resp) => {
                    return fetchCompanyTurnoversByObjectsSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCompanyTurnoversByObjectsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.turnoversRequestFailed')),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const companyTurnoversByPeriodDownloadEpic: Epic<CompanyDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD),
        exhaustMap((action) => {
            const params = action.payload;
            const taxNumber = state$.value.routerState.route.params.CompanyTaxNumber;
            const fileName = `taxpayer_${taxNumber}_TurnoversByPeriod.xlsx`;

            return from(companiesClient.downloadCompanyTurnoversByPeriodAsExcel(taxNumber, params)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadTurnoversByPeriodSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadTurnoversByPeriodErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const companyTurnoversByObjectsDownloadEpic: Epic<CompanyDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_OBJECTS),
        exhaustMap(() => {
            const params = { ...state$.value.companyDetailsReducer.turnoversByObjectsFilterParams };
            const taxNumber = state$.value.routerState.route.params.CompanyTaxNumber;
            const fileName = `taxpayer_${taxNumber}_TurnoversByObjects.xlsx`;

            return from(companiesClient.downloadCompanyTurnoversByObjectsAsExcel(taxNumber, params)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadTurnoversByObjectsSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadTurnoversByObjectsErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};
